/**
 * Loader del widget de Condato Atlas.
 *
 * Corre en el origen del cliente, y por eso lleva el peso del arranque: el `Origin` de una petición
 * es siempre el del documento que la hace, así que solo desde aquí el navegador acredita el dominio
 * real ante el servidor. De ahí el reparto: el loader posee la identidad del visitante y la
 * credencial de sesión, y entrega al panel lo justo para conversar. La identidad de vida larga
 * nunca entra en el iframe.
 *
 * Contrato de integración, y no cambia:
 *
 *   <script src="https://…/v1/atlas-widget.js" data-atlas-id="pub_…" async></script>
 *
 * El identificador **no es una credencial**: viaja en el HTML de una web pública.
 */

import { WidgetError, type SessionResponse } from '../shared/api';
import { normalizeHexColor } from '../shared/color';
import { normalizeImageUrl } from '../shared/url';
import {
  HOST_SOURCE,
  PROTOCOL_VERSION,
  isPanelMessage,
  type HostMessage,
  type PanelBootstrap
} from '../shared/protocol';
import { startSession } from '../shared/session';
import { Launcher } from './launcher';
import { readVisitor, writeVisitor } from './visitor-storage';

const LABELS = {
  open: 'Abrir el chat de asistencia',
  close: 'Cerrar el chat de asistencia',
  frame: 'Chat de asistencia'
};

/**
 * Referencia al `<script>` que nos ha cargado. Se captura en el ámbito del módulo porque
 * `document.currentScript` solo vale durante la ejecución síncrona y en un callback ya es `null`.
 */
const currentScript = document.currentScript as HTMLScriptElement | null;

/**
 * Estado del arranque. Cargar el iframe y pedir la sesión ocurren en paralelo, así que se guardan
 * los dos resultados y se entrega el arranque cuando están ambos.
 */
interface Boot {
  bootstrap: PanelBootstrap | null;
  failure: string | null;
  panelReady: boolean;
  requested: boolean;

  /** Hay una renovación de credencial en vuelo. Evita pedir dos por un turno. */
  renewing: boolean;
}

function start(script: HTMLScriptElement): void {
  const publicId = script.dataset['atlasId']?.trim();

  if (!publicId) {
    // Un aviso y nada más. El widget no puede funcionar, pero tampoco puede romper la web de
    // nadie por un atributo mal puesto.
    console.warn('[atlas] Falta el atributo data-atlas-id en el script del widget.');
    return;
  }

  // El origen del panel sale del `src` de este mismo script: así el bundle sirve para cualquier
  // host sin recompilar y nunca apunta a un entorno distinto del que lo sirvió.
  const origin = new URL(script.src, window.location.href).origin;
  const visitor = readVisitor(publicId);

  const boot: Boot = {
    bootstrap: null,
    failure: null,
    panelReady: false,
    requested: false,
    renewing: false
  };

  const launcher = new Launcher({
    panelUrl: `${origin}/panel/index.html`,
    labels: LABELS,
    onOpen: () => {
      void openSession(publicId, boot, launcher, origin);
      deliver(boot, launcher, origin, 'shown');
    },
    onClose: () => undefined
  });

  // Este orden importa: manda el último color que confirmó el servidor y el del snippet solo sirve
  // para la primera visita, para que la burbuja no cambie de color al abrir el panel.
  launcher.setPrimaryColor(
    normalizeHexColor(visitor.color) ?? normalizeHexColor(script.dataset['atlasColor'])
  );

  // Con el avatar, igual, salvo que aquí el servidor también puede confirmar que no hay ninguno.
  launcher.setAvatar(normalizeImageUrl(visitor.avatar ?? script.dataset['atlasAvatar']));

  listenToPanel(publicId, boot, launcher, origin);
}

/**
 * Pide la sesión la primera vez que se abre el chat, no al cargar la página: así no hay una llamada
 * al API ni un visitante creado por cada visita a cada página de la web del cliente.
 */
async function openSession(publicId: string, boot: Boot, launcher: Launcher, origin: string): Promise<void> {
  if (boot.requested) return;
  boot.requested = true;

  try {
    const session = await startSession(publicId, readVisitor(publicId).token);

    remember(publicId, launcher, session);

    boot.bootstrap = {
      publicId,
      sessionToken: session.sessionToken,
      streamBase: session.streamBase,
      presentation: session.presentation,
      limits: session.limits,
      conversation: session.conversation
    };
  } catch (error) {
    boot.failure = reasonFor(error);

    // Se permite reintentar en la siguiente apertura: un fallo de red no debería dejar el chat
    // inservible para el resto de la visita.
    boot.requested = false;
  }

  deliver(boot, launcher, origin);
}

/**
 * Emite una credencial de sesión nueva para un panel que ya está abierto.
 *
 * La pide el loader por el mismo motivo que la primera, pero solo se le entrega la credencial: el
 * panel ya tiene pintada la presentación y el hilo. Para el visitante es transparente.
 */
async function renewSession(publicId: string, boot: Boot, launcher: Launcher, origin: string): Promise<void> {
  // Dos renovaciones a la vez para el mismo visitante dejarían una credencial huérfana.
  if (boot.renewing) return;
  boot.renewing = true;

  try {
    const session = await startSession(publicId, readVisitor(publicId).token);

    remember(publicId, launcher, session);

    send(launcher, origin, {
      source: HOST_SOURCE,
      version: PROTOCOL_VERSION,
      type: 'session',
      sessionToken: session.sessionToken,
      streamBase: session.streamBase
    });
  } catch (error) {
    // El panel está esperando: sin respuesta se queda con el turno en el aire hasta agotar su plazo.
    send(launcher, origin, {
      source: HOST_SOURCE,
      version: PROTOCOL_VERSION,
      type: 'unavailable',
      reason: reasonFor(error)
    });
  } finally {
    boot.renewing = false;
  }
}

/**
 * Guarda lo que hay que recordar de una sesión y actualiza el color y el avatar del lanzador. Se
 * guarda en el origen del cliente: el almacenamiento del iframe lo bloquea Safari y lo particiona
 * Chrome.
 */
function remember(publicId: string, launcher: Launcher, session: SessionResponse): void {
  writeVisitor(publicId, { token: session.visitorToken });

  const color = normalizeHexColor(session.presentation.primaryColor);

  if (color) {
    writeVisitor(publicId, { color });
    launcher.setPrimaryColor(color);
  }

  // Sin avatar también se recuerda: si el administrador lo quita, la burbuja deja de mostrarlo
  // aunque el snippet pegado en la web siga trayéndolo.
  const avatar = normalizeImageUrl(session.presentation.avatarUrl);

  writeVisitor(publicId, { avatar: avatar ?? '' });
  launcher.setAvatar(avatar);
}

/** Motivo que se le puede contar al visitante. Un fallo inesperado no revela nada. */
function reasonFor(error: unknown): string {
  return error instanceof WidgetError ? error.message : 'El chat no está disponible en este momento.';
}

/**
 * Entrega al panel lo que ya se pueda entregar. Se llama desde los tres sitios que pueden completar
 * el arranque —apertura, respuesta de la sesión y aviso del panel—, porque cualquiera puede ser el
 * último.
 */
function deliver(boot: Boot, launcher: Launcher, origin: string, extra?: 'shown'): void {
  if (!boot.panelReady) return;

  if (boot.bootstrap) {
    send(launcher, origin, { source: HOST_SOURCE, version: PROTOCOL_VERSION, type: 'bootstrap', bootstrap: boot.bootstrap });
    boot.bootstrap = null;
  } else if (boot.failure) {
    send(launcher, origin, { source: HOST_SOURCE, version: PROTOCOL_VERSION, type: 'unavailable', reason: boot.failure });
    boot.failure = null;
  }

  if (extra === 'shown') {
    send(launcher, origin, { source: HOST_SOURCE, version: PROTOCOL_VERSION, type: 'shown' });
  }
}

/**
 * Envía un mensaje al panel, si ya hay a quién enviárselo.
 *
 * Hasta que el panel no avisa de que está listo, su ventana sigue siendo `about:blank` y un mensaje
 * dirigido a nuestro origen falla con un error en la consola de la web del cliente. De ahí que todo
 * pase por `deliver`.
 */
function send(launcher: Launcher, origin: string, message: HostMessage): void {
  // El destino se declara explícito: con `*` el mensaje se entregaría a cualquier documento que
  // hubiera acabado en ese iframe.
  launcher.panelWindow?.postMessage(message, origin);
}

function listenToPanel(publicId: string, boot: Boot, launcher: Launcher, origin: string): void {
  window.addEventListener('message', (event: MessageEvent) => {
    // Las tres comprobaciones son necesarias: sin la del origen, cualquier script de la página
    // puede fabricar mensajes, que es el agujero clásico de los widgets embebidos.
    if (event.origin !== origin) return;
    if (event.source !== launcher.panelWindow) return;
    if (!isPanelMessage(event.data)) return;

    switch (event.data.type) {
      case 'ready':
        boot.panelReady = true;
        deliver(boot, launcher, origin, 'shown');
        break;

      case 'close':
        launcher.close();
        break;

      case 'renew':
        void renewSession(publicId, boot, launcher, origin);
        break;
    }
  });
}

/**
 * Arranca cuando el documento tenga `body`: el snippet se recomienda con `async`, pero nada impide
 * pegarlo en el `<head>` sin él, y entonces insertar el contenedor fallaría.
 */
if (currentScript) {
  if (document.body) {
    start(currentScript);
  } else {
    document.addEventListener('DOMContentLoaded', () => start(currentScript), { once: true });
  }
}
